import { occupancyDayKey, currentOccupancyDayKey } from "../occupancyDay";

/**
 * Shared NPL occupancy math from hourly_stats (GCP /v1/temp/deviceTest/text/test).
 * Live strip/map prefers /v1/occupancy so vacancy matches the Pi LED.
 * Chart series still uses this 04:00 Israel + baseline + clamp.
 */

export const NPL_PRE_OCCUPANCY = {
  "AHAD_HA'AM": 5,
  SAN_MARTIN: 1,
  ZVI: 1,
  BERNSTEIN: 1,
  CENTER_OF_THE_PARK: 1,
  SHAKH: 1,
  CHEN: 1,
};

export const NPL_CAPACITY = {
  "AHAD_HA'AM": 124,
  SAN_MARTIN: 132,
  ZVI: 102,
  BERNSTEIN: 140,
  CENTER_OF_THE_PARK: 100,
  SHAKH: 70,
  CHEN: 70,
};

/** Physical sub-lots inside a logical zone (map pins). Capacities must sum to zone. */
export const NPL_SUB_LOTS = {
  CENTER_OF_THE_PARK: {
    horse_farm: { capacity: 60, baseline: 1 },
    volleyball: { capacity: 40, baseline: 0 },
  },
};

function clampOccupancy(raw, lotKey) {
  const capacity = NPL_CAPACITY[lotKey] ?? 0;
  return Math.min(capacity, Math.max(0, raw));
}

function clampToCapacity(raw, capacity) {
  const cap = Math.max(0, Number(capacity) || 0);
  return Math.min(cap, Math.max(0, raw));
}

/**
 * Hourly rows → occupancy series (reset at 04:00 Israel + overnight baseline).
 * Running net is signed like Pi (extra OUTs stay as debt). Only the plotted
 * occupancy is clamped 0…capacity, so the last point matches the strip.
 */
export function buildOccupancySeries(hourlyRows = [], lotKey) {
  const baseline = NPL_PRE_OCCUPANCY[lotKey] ?? 0;
  let currentDay = null;
  let cum = baseline;
  return (hourlyRows || []).map((item) => {
    const dayKey = occupancyDayKey(item.date);
    if (dayKey !== currentDay) {
      currentDay = dayKey;
      cum = baseline;
    }
    const net = (Number(item.entering) || 0) - (Number(item.leaving) || 0);
    cum += net;
    return {
      ...item,
      occupancy: clampOccupancy(cum, lotKey),
    };
  });
}

/**
 * Live occupancy = last point of today's series (Pi day net, LED clamp).
 */
export function currentOccupancyFromHourly(hourlyRows = [], lotKey) {
  const series = buildOccupancySeries(hourlyRows, lotKey);
  const today = currentOccupancyDayKey();
  let last = clampOccupancy(NPL_PRE_OCCUPANCY[lotKey] ?? 0, lotKey);
  for (const row of series) {
    if (occupancyDayKey(row.date) === today) {
      last = row.occupancy;
    }
  }
  return last;
}

/** Vacancy for strip/map: capacity − clamped occupancy (0 … capacity). */
export function vacancyFromHourly(hourlyRows = [], lotKey) {
  const capacity = NPL_CAPACITY[lotKey] ?? 0;
  return capacity - currentOccupancyFromHourly(hourlyRows, lotKey);
}

/**
 * Signed day net per sub-lot (may be negative when OUTs exceed INs).
 */
export function signedSubLotNets(hourlyRows = [], lotKey) {
  const cfgs = NPL_SUB_LOTS[lotKey];
  if (!cfgs) return {};
  const today = currentOccupancyDayKey();
  const out = {};
  Object.entries(cfgs).forEach(([subLotKey, cfg]) => {
    let cum = Number(cfg.baseline) || 0;
    for (const row of hourlyRows || []) {
      if (occupancyDayKey(row.date) !== today) continue;
      const sl = row.subLots?.[subLotKey] || {};
      cum += (Number(sl.entering) || 0) - (Number(sl.leaving) || 0);
    }
    out[subLotKey] = cum;
  });
  return out;
}

/**
 * Map pin occupancies for a multi-pin zone.
 *
 * Independent clamp would show horse 22 + volleyball 0 while the strip/Pi
 * shows 11 (volleyball OUT debt cancels cars elsewhere). Extra OUTs on one
 * sub-lot are applied as debt against sibling positives, then the result is
 * scaled to the zone strip total so pin numbers always sum to the strip.
 */
export function allocatedSubLotOccupancy(
  hourlyRows = [],
  lotKey,
  zoneOccupied = null
) {
  const cfgs = NPL_SUB_LOTS[lotKey];
  if (!cfgs) return {};

  const signed = signedSubLotNets(hourlyRows, lotKey);
  const keys = Object.keys(cfgs);
  const positives = {};
  let posSum = 0;
  let debt = 0;

  keys.forEach((k) => {
    const s = Number(signed[k]) || 0;
    if (s > 0) {
      positives[k] = s;
      posSum += s;
    } else {
      positives[k] = 0;
      debt += s;
    }
  });

  const allocated = {};
  keys.forEach((k) => {
    const cap = cfgs[k].capacity;
    const raw =
      posSum > 0 && positives[k] > 0
        ? positives[k] + debt * (positives[k] / posSum)
        : 0;
    allocated[k] = clampToCapacity(raw, cap);
  });

  const zoneCap = NPL_CAPACITY[lotKey] ?? 0;
  const target =
    zoneOccupied == null || Number.isNaN(Number(zoneOccupied))
      ? clampToCapacity(
          keys.reduce((acc, k) => acc + (Number(signed[k]) || 0), 0),
          zoneCap
        )
      : clampToCapacity(Number(zoneOccupied), zoneCap);

  const sum = keys.reduce((acc, k) => acc + allocated[k], 0);
  if (target <= 0) {
    keys.forEach((k) => {
      allocated[k] = 0;
    });
    return allocated;
  }
  if (sum <= 0) {
    // No positive nets — put zone total on the largest sub-lot capacity.
    const primary = keys.reduce((a, b) =>
      (cfgs[a].capacity || 0) >= (cfgs[b].capacity || 0) ? a : b
    );
    keys.forEach((k) => {
      allocated[k] = k === primary ? clampToCapacity(target, cfgs[k].capacity) : 0;
    });
    return allocated;
  }
  if (sum === target) return allocated;

  let remaining = target;
  const positiveKeys = keys.filter((k) => allocated[k] > 0);
  positiveKeys.forEach((k, i) => {
    if (i === positiveKeys.length - 1) {
      allocated[k] = clampToCapacity(remaining, cfgs[k].capacity);
      return;
    }
    const share = Math.round((allocated[k] / sum) * target);
    allocated[k] = clampToCapacity(share, cfgs[k].capacity);
    remaining -= allocated[k];
  });
  keys.forEach((k) => {
    if (!positiveKeys.includes(k)) allocated[k] = 0;
  });
  return allocated;
}

/** @deprecated Prefer allocatedSubLotOccupancy so pins sum to the strip. */
export function currentSubLotOccupancy(hourlyRows = [], lotKey, subLotKey) {
  const all = allocatedSubLotOccupancy(hourlyRows, lotKey);
  return all[subLotKey] ?? null;
}

export function subLotCapacity(lotKey, subLotKey) {
  return NPL_SUB_LOTS[lotKey]?.[subLotKey]?.capacity ?? 0;
}
